import type { UserRole } from '../../types';
import { ehComercialInterno, funcaoComercial } from './departamentos.ts';
import { montarRepasse } from './comissaoIndicacaoPosVenda.ts';
import type { RepassePosVenda } from './comissaoIndicacaoPosVenda.ts';

// ⚠️ Este arquivo é 100% puro DE PROPÓSITO — nenhuma função aqui toca banco,
// nenhum import de supabase. É o que permite testar a lógica de maior risco
// (fórmula de comissão) com node:test puro, sem servidor: `import.meta.env`
// não existe fora do Vite, e um `import { supabase }` no topo derrubaria
// qualquer teste que importasse até a função mais pura deste arquivo. As duas
// únicas funções que precisavam de banco (recalcularCondicaoComercial,
// carregarModalidadePlano) moraram aqui até 09/09/2026 e foram pra
// politicaComercialDb.ts — se uma função nova aqui precisar de supabase, ela
// vai pra lá, não aqui.

// ==========================================
// NOVA POLÍTICA COMERCIAL — VENDEDOR EXTERNO (jul/2026)
// Fonte única: modal Alterar Plano, Folha de Pagamento,
// página Minha Comissão e simuladores.
// ==========================================

// A partir desta competência o fechamento do vendedor externo
// usa o modelo novo (ativação R$/kWh + threshold + rede + recorrência).
export const COMPETENCIA_VIRADA = { mes: 7, ano: 2026 } as const;

export function isCompetenciaNovaPolitica(mes: number, ano: number): boolean {
  return ano > COMPETENCIA_VIRADA.ano ||
    (ano === COMPETENCIA_VIRADA.ano && mes >= COMPETENCIA_VIRADA.mes);
}

// Itens da v2 ainda em discussão com a diretoria (jul/2026): bônus de safra
// 1ª fatura, SLA/clawback, premiação trimestral, página Minha Comissão e
// vínculo "Captado por". false = ocultos da UI; o motor de cálculo e os
// dados continuam intactos por trás — reative virando este flag.
export const MOSTRAR_ITENS_V2_EM_DISCUSSAO = false;

// ==========================================
// MODALIDADE DO PLANO (qual formulário/modelo a negociação usa)
// ==========================================
// Carimbada na criação a partir do cargo de quem criou (negociacoes.plano_modalidade,
// migration 20260730120000). A NEGOCIAÇÃO manda — o cargo de quem está olhando não
// entra na conta, senão parceiro e gerente veem (e gravam) modelos diferentes no
// mesmo registro. Transferir o responsável não muda a modalidade.

export type ModalidadePlano = 'parceiro' | 'vendedor' | 'vendedor_interno';

export interface PerfilParaModalidade {
  role?: UserRole | null;
  departamento?: string | null;
  departamentos?: string[] | null;
  funcao_comercial?: string | null;
}

/** @deprecated Só enxerga role — não pega o Comercial Interno (departamento,
 *  não role: a Júlia Cintra é `analista`). Mantido para os pouquíssimos
 *  chamadores que só têm o role em mãos; prefira modalidadePorPerfil. */
export function modalidadePorRole(role: UserRole | null | undefined): ModalidadePlano {
  return role === 'parceiro' || role === 'vendedor_parceiro' ? 'parceiro' : 'vendedor';
}

/**
 * Parceiro e vendedor parceiro usam o formulário simples de percentuais — não têm
 * multiplicador (a remuneração vem da rede de parceiros, ver comissaoExternaV2).
 * Comercial Interno (Closer e BDR pela função; sem função, o departamento —
 * checado por ehComercialInterno, que cobre role != 'vendedor', caso da Júlia)
 * usa o formulário de desconto direto (ver seção COMERCIAL INTERNO abaixo). A
 * negociação aberta pelo BDR nasce aqui porque quem fecha é a Closer. A gerente
 * comercial (papel `gerente`, sem função) também: ela abre negociação para tratar
 * ou passar à equipe, que é quase toda Closer e BDR — e pode escolher o modelo do
 * vendedor externo na criação (podeEscolherModalidadeNaCriacao). Todo o resto,
 * inclusive o Hunter, cai no vendedor externo (planos × público). Role vence
 * departamento nesse desempate: um parceiro cadastrado por engano com
 * departamento Comercial Interno continua com o formulário de parceiro, que é o
 * que a rede dele exige. Espelha set_plano_modalidade no banco.
 */
export function modalidadePorPerfil(perfil: PerfilParaModalidade | null | undefined): ModalidadePlano {
  if (!perfil) return 'vendedor';
  if (perfil.role === 'parceiro' || perfil.role === 'vendedor_parceiro') return 'parceiro';
  if (ehComercialInterno(perfil)) return 'vendedor_interno';
  if (perfil.role === 'gerente' && !funcaoComercial(perfil)) return 'vendedor_interno';
  return 'vendedor';
}

/**
 * Quem cria a negociação pode escolher o modelo do plano? Só a gerência comercial
 * sem função própria: ela trata a negociação ou passa para Closer, BDR ou Hunter, e
 * o modelo tem de ser o de quem vai fechar. Os demais seguem o próprio modelo.
 */
export function podeEscolherModalidadeNaCriacao(perfil: PerfilParaModalidade | null | undefined): boolean {
  return !!perfil && perfil.role === 'gerente' && !funcaoComercial(perfil);
}

export interface NegociacaoModalidade {
  plano_modalidade?: string | null;
  responsavel?: PerfilParaModalidade | null;
}

export const MODALIDADES_VALIDAS: ModalidadePlano[] = ['parceiro', 'vendedor', 'vendedor_interno'];

/**
 * Modalidade do plano de uma negociação existente. `viewer` só é usado no
 * fluxo de CRIAÇÃO (negociacao null), quando quem está na tela é o próprio criador.
 */
export function resolverModalidadePlano(
  negociacao: NegociacaoModalidade | null | undefined,
  viewer?: PerfilParaModalidade | null
): ModalidadePlano {
  if (negociacao) {
    if (MODALIDADES_VALIDAS.includes(negociacao.plano_modalidade as ModalidadePlano)) {
      return negociacao.plano_modalidade as ModalidadePlano;
    }
    // Negociação anterior à coluna (ou criada fora do trigger): cai no cargo/departamento do responsável.
    if (negociacao.responsavel) return modalidadePorPerfil(negociacao.responsavel);
    return 'vendedor';
  }
  return modalidadePorPerfil(viewer);
}

// ==========================================
// PÚBLICO (perfil de consumo do cliente)
// ==========================================

export type Publico = 'A' | 'B' | 'C';

export const PUBLICOS: Record<Publico, { label: string; faixaLabel: string; min: number; max: number }> = {
  A: { label: 'Público A', faixaLabel: 'Acima de 8.000 kWh', min: 8000, max: Infinity },
  B: { label: 'Público B', faixaLabel: 'De 1.501 a 7.999 kWh', min: 1501, max: 7999 },
  C: { label: 'Público C', faixaLabel: 'Até 1.500 kWh', min: 0, max: 1500 },
};

export function classificarPublico(volumeKwh: number): Publico {
  if (volumeKwh > 7999) return 'A';
  if (volumeKwh >= 1501) return 'B';
  return 'C';
}

// ==========================================
// PLANOS DA POLÍTICA (matriz de multiplicadores)
// ==========================================

export interface PlanoComercial {
  codigo: string;
  label: string;
  descTarifa: number;    // % desconto na tarifa
  descBandeira: number;  // % desconto na bandeira
  mult: Record<Publico, number>;
  foraDaPolitica: boolean;
  /** Plano descontinuado: continua resolvendo o histórico (getPlano e
   *  inferirPlanoPorPercentuais varrem a lista inteira) mas não é ofertável
   *  em venda nova — ver planosSelecionaveis(). */
  legado?: boolean;
  /** Plano criado na virada de jul/2026: não pode ser inferido a partir dos
   *  percentuais de uma negociação anterior a ele — ver inferirPlanoHistorico(). */
  novo?: boolean;
  /** Fidelidade não escalona por prazo neste plano: 0,90 sem contrato de
   *  permanência, 1,00 com qualquer prazo — ver getFatorFidelidade(). */
  fidelidadeBinaria?: boolean;
}

// jul/2026: "20% sem fidelidade" dá lugar ao "20% + 50% bandeira", que passa a
// conceder metade da bandeira. A recalibração da matriz que ficou pendente naquele
// split foi aprovada (ago/2026): o 20_50_bandeira sai de 0,80/0,90/1,00 para
// 1,20/1,50/1,80 e passa a ser o plano MAIS rentável para o vendedor — menor
// desconto ao cliente, maior comissão. Em troca, é o único que não escalona
// fidelidade por prazo (fidelidadeBinaria). Os demais planos seguem inalterados,
// inclusive na escala de fidelidade.
export const PLANOS: PlanoComercial[] = [
  { codigo: '20_50_bandeira', label: '20% + 50% bandeira', descTarifa: 20, descBandeira: 50, mult: { C: 1.20, B: 1.50, A: 1.80 }, foraDaPolitica: false, novo: true, fidelidadeBinaria: true },
  { codigo: '22_50_bandeira', label: '22% + 50% bandeira', descTarifa: 22, descBandeira: 50, mult: { C: 1.10, B: 1.35, A: 1.50 }, foraDaPolitica: false },
  { codigo: '24_50_bandeira', label: '24% + 50% bandeira', descTarifa: 24, descBandeira: 50, mult: { C: 0.80, B: 1.15, A: 1.35 }, foraDaPolitica: false },
  { codigo: '25_100_bandeira', label: '25% + 100% bandeira', descTarifa: 25, descBandeira: 100, mult: { C: 0.50, B: 0.95, A: 1.25 }, foraDaPolitica: false },
  { codigo: '28_100_bandeira', label: '28% + 100% bandeira', descTarifa: 28, descBandeira: 100, mult: { C: 0.30, B: 0.45, A: 0.70 }, foraDaPolitica: false },
  { codigo: 'fora_da_politica', label: 'Fora da política / acima de 30%', descTarifa: 0, descBandeira: 0, mult: { C: 0.30, B: 0.30, A: 0.30 }, foraDaPolitica: true },

  // ── Descontinuados ─────────────────────────────────────────────────────────
  // Ficam no FIM da lista de propósito: o `find` de inferirPlanoPorPercentuais
  // sempre prefere um plano ativo em caso de colisão futura de percentuais.
  // Nunca remover — negociações antigas guardam este código em plano_codigo e
  // getPlano() nulo cairia no fallback silencioso de 0,30x.
  // 20% sem fidelidade → substituído por 20_50_bandeira em jul/2026.
  { codigo: '20_sem_fidelidade', label: '20% sem fidelidade', descTarifa: 20, descBandeira: 0, mult: { C: 0.80, B: 0.90, A: 1.00 }, foraDaPolitica: false, legado: true },
];

export function getPlano(codigo: string | null | undefined): PlanoComercial | null {
  if (!codigo) return null;
  return PLANOS.find(p => p.codigo === codigo) ?? null;
}

/** Planos ofertáveis hoje — é o que os seletores devem listar. `codigoAtual`
 *  reexibe o legado quando ele já é o plano gravado/ativo, para o usuário
 *  enxergar a condição do contrato sem poder voltar para ela depois de sair. */
export function planosSelecionaveis(codigoAtual?: string | null): PlanoComercial[] {
  return PLANOS.filter(p => !p.legado || p.codigo === codigoAtual);
}

export function getMultiplicadorPlano(codigo: string, publico: Publico): number {
  return getPlano(codigo)?.mult[publico] ?? 0.30;
}

// Identifica o plano oficial a partir dos percentuais gravados nas UCs
// (percentuais em escala 0–100). Inclui planos legados de propósito: é o que
// impede uma negociação antiga de 20%/0% de ser lida como "fora da política"
// (0,30x) depois da virada de plano. Use só para exibição — a UI precisa
// reconhecer o plano novo assim que o vendedor o seleciona em venda nova.
export function inferirPlanoPorPercentuais(descTarifa: number, descBandeira: number): PlanoComercial | null {
  return PLANOS.find(p => !p.foraDaPolitica && p.descTarifa === Math.round(descTarifa) && p.descBandeira === Math.round(descBandeira)) ?? null;
}

/**
 * Mesma inferência, mas para REGISTROS ANTERIORES à política vigente: exclui os
 * planos criados na virada. Regra velha para negociação velha.
 *
 * O par 20%/50% era o teto histórico aprovado sem plano nenhum (ver
 * discountApproval) — deixá-lo casar com o 20_50_bandeira daria retroativamente
 * a essas negociações um multiplicador que nunca foi acordado. Depois da
 * recalibração de ago/2026 o buraco ficou grande: seria pagar 1,20–1,80 onde se
 * acordou 0,80–1,00, em cima da carteira antiga inteira.
 * Venda nova sempre grava plano_codigo explícito (wizard e modal), então a
 * inferência só serve ao passado: é esta que os caminhos de dinheiro usam —
 * o motor da folha e a pré-seleção do modal Alterar Plano.
 */
export function inferirPlanoHistorico(descTarifa: number, descBandeira: number): PlanoComercial | null {
  const plano = inferirPlanoPorPercentuais(descTarifa, descBandeira);
  return plano && !plano.novo ? plano : null;
}

// ==========================================
// CONDIÇÃO ESPECIAL (fora da política) — vendedor externo
// ==========================================
// set/2026: muitos pedidos "fora da política" eram erro de preenchimento — o
// campo livre aceitava 0–100%, inclusive percentuais de plano oficial (28/100)
// ou abaixo da política (20/0). A condição especial passa a ter degraus fixos,
// todos acima do maior plano oficial, e fica escondida atrás de um aviso na tela.
// Valores iniciais propostos — ajustar aqui quando a diretoria fechar a régua.

// Tarifa de 1 em 1 a partir de 30% (primeiro degrau acima do maior plano
// oficial, 28%) e bandeira de 5 em 5 — pedido do dono do produto, 17/09/2026.
export const CONDICAO_ESPECIAL_TARIFA_MIN = 30;
export const CONDICAO_ESPECIAL_TARIFA_MAX = 50;

export const CONDICAO_ESPECIAL_TARIFAS: number[] = Array.from(
  { length: CONDICAO_ESPECIAL_TARIFA_MAX - CONDICAO_ESPECIAL_TARIFA_MIN + 1 },
  (_, i) => CONDICAO_ESPECIAL_TARIFA_MIN + i
);

export const CONDICAO_ESPECIAL_BANDEIRAS: number[] = Array.from(
  { length: 21 },
  (_, i) => i * 5
);

export function isOpcaoCondicaoEspecial(descTarifa: number, descBandeira: number): boolean {
  return CONDICAO_ESPECIAL_TARIFAS.includes(Math.round(descTarifa))
    && CONDICAO_ESPECIAL_BANDEIRAS.includes(Math.round(descBandeira));
}

/**
 * Autocorreção do plano: "fora da política" (ou nenhum plano) com percentuais
 * que batem com um plano oficial ATIVO vira esse plano — não gera pedido de
 * aprovação nem derruba o multiplicador para 0,30x. Legado não entra: venda
 * nova não pode voltar para plano descontinuado. Sem correção, devolve o
 * código recebido.
 */
export function corrigirPlanoPorPercentuais(
  planoCodigo: string | null | undefined,
  descTarifa: number,
  descBandeira: number
): string | null {
  const atual = getPlano(planoCodigo);
  if (atual && !atual.foraDaPolitica) return atual.codigo;
  const oficial = inferirPlanoPorPercentuais(descTarifa, descBandeira);
  if (oficial && !oficial.legado) return oficial.codigo;
  return planoCodigo ?? null;
}

// ==========================================
// FIDELIDADE (prazo contratual)
// ==========================================

// Escala PADRÃO, válida para todos os planos menos os marcados com
// `fidelidadeBinaria` — ver getFatorFidelidade. Não leia o `fator` daqui para
// exibir na tela: use fidelidadeOpcoesDoPlano(), que resolve pelo plano.
export const FIDELIDADE_OPCOES = [
  { valor: 'Sem fidelidade', label: 'Isento de fidelidade', fator: 0.90 },
  { valor: '12 meses', label: '12 meses', fator: 1.00 },
  { valor: '24 meses', label: '24 meses', fator: 1.10 },
  { valor: '36 meses', label: '36 meses', fator: 1.20 },
] as const;

/** "Sem fidelidade" escolhido de fato. Vazio NÃO conta: é negociação que nunca
 *  passou pelo plano (o fator trata vazio como isento, a aprovação não — 6532). */
export function isFidelidadeIsenta(fidelidade?: string | null): boolean {
  return !!fidelidade && /sem\s*fidelidade/i.test(fidelidade);
}

/**
 * Fator de fidelidade — depende do PLANO desde ago/2026.
 *
 * Padrão: escalona por prazo (sem 0,90 · 12m 1,00 · 24m 1,10 · 36m 1,20).
 * Planos `fidelidadeBinaria` (hoje só o 20/50): apenas ter ou não ter contrato
 * de permanência — 0,90 sem fidelidade, 1,00 com qualquer prazo. Eles já entram
 * com os maiores multiplicadores da matriz e não acumulam bônus de prazo em cima.
 *
 * Sem `planoCodigo` cai na escala padrão — é o que preserva o cálculo das
 * negociações antigas, que não gravaram plano. Fidelidade ausente = 0,90.
 * O legado '18 meses' não tem fator próprio e cai em 1,00 pelo parse de meses.
 */
export function getFatorFidelidade(
  fidelidade: string | null | undefined,
  planoCodigo?: string | null
): number {
  const semFidelidade = !fidelidade || /sem\s*fidelidade/i.test(fidelidade);

  if (getPlano(planoCodigo)?.fidelidadeBinaria) return semFidelidade ? 0.90 : 1.00;
  if (semFidelidade) return 0.90;

  const opcao = FIDELIDADE_OPCOES.find(o => o.valor.toLowerCase() === fidelidade!.toLowerCase());
  if (opcao) return opcao.fator;

  const meses = parseInt(fidelidade!.match(/\d+/)?.[0] ?? '0', 10);
  if (meses >= 36) return 1.20;
  if (meses >= 24) return 1.10;
  return 1.00;
}

/** Opções de fidelidade com o fator já resolvido para o plano — é isto que as
 *  telas devem listar, para o vendedor ver o fator que de fato vai valer. */
export function fidelidadeOpcoesDoPlano(planoCodigo?: string | null) {
  return FIDELIDADE_OPCOES.map(o => ({
    valor: o.valor as string,
    label: o.label as string,
    fator: getFatorFidelidade(o.valor, planoCodigo),
  }));
}

// ==========================================
// CARÊNCIA (para solicitações — sai no Plano de Benefícios/termo)
// ==========================================
// Padrão comercial: sem carência. Qualquer prazo diferente disso exige
// aprovação do gerente/admin (ver discountApproval.ts). Fonte única usada
// pelo modal "Alterar Plano" e pela etapa "Definição do Plano" (Nova Negociação).

export const CARENCIA_OPCOES = [
  { valor: 'sem carência', label: 'Sem carência' },
  { valor: 'até 30 dias (1 mês)', label: 'Até 30 dias' },
  { valor: 'até 60 dias (2 meses)', label: 'Até 60 dias' },
  // "até 90 dias (3 meses)" saiu das opções a pedido da gestão (#6286).
] as const;

const ROTULO_CARENCIA_ANTIGA: Record<string, string> = {
  'até 90 dias (3 meses)': 'Até 90 dias',
};

/**
 * Opções para a tela, mantendo a carência que a UC JÁ TEM quando ela não é
 * mais oferecida (#6286): sem isso o Alterar Plano abriria sem nada marcado e
 * o valor antigo trocaria sem ninguém escolher. Aparece só enquanto for o valor
 * atual — escolhida outra opção, some.
 */
export function carenciaOpcoesCom(
  atual: string | null | undefined,
): { valor: string; label: string }[] {
  const opcoes: { valor: string; label: string }[] = [...CARENCIA_OPCOES];
  const valor = (atual ?? '').trim();
  if (!isCarenciaSolicitada(valor) || opcoes.some(o => o.valor === valor)) return opcoes;
  return [...opcoes, { valor, label: `${ROTULO_CARENCIA_ANTIGA[valor] ?? valor} (atual)` }];
}

export function isCarenciaSolicitada(carencia: string | null | undefined): boolean {
  return !!carencia && !/sem\s*car/i.test(carencia);
}

// ==========================================
// MULTIPLICADOR FINAL DA NEGOCIAÇÃO
// ==========================================

export interface MultiplicadorResultado {
  publico: Publico;
  multPlano: number;
  fatorFidelidade: number;
  final: number;
}

export function calcularMultiplicadorFinal(
  planoCodigo: string,
  volumeKwh: number,
  fidelidade: string | null | undefined
): MultiplicadorResultado {
  const publico = classificarPublico(volumeKwh);
  const multPlano = getMultiplicadorPlano(planoCodigo, publico);
  const fatorFidelidade = getFatorFidelidade(fidelidade, planoCodigo);
  return {
    publico,
    multPlano,
    fatorFidelidade,
    final: Math.round(multPlano * fatorFidelidade * 100) / 100,
  };
}

// ==========================================
// COMISSÃO DE ATIVAÇÃO POR VOLUME (R$/kWh)
// ==========================================

export const META_MENSAL_KWH = 25000;

// Faixas por % de atingimento da meta (ajuste Bruno 13/07/2026):
// não bateu → 0,08 · bateu (100%) → 0,11 · 120% → 0,13 · 150%+ → 0,17.
export const ATIVACAO_FAIXAS = [
  { minPct: 0, rate: 0.08, label: 'Abaixo da meta (< 100%)' },
  { minPct: 100, rate: 0.11, label: 'Meta batida (100% a 119%)' },
  { minPct: 120, rate: 0.13, label: '120% a 149% da meta' },
  { minPct: 150, rate: 0.17, label: '150% da meta ou mais' },
] as const;

export function getAtivacaoRate(volumeMesKwh: number, metaKwh: number = META_MENSAL_KWH): number {
  const meta = metaKwh > 0 ? metaKwh : META_MENSAL_KWH;
  const pct = (volumeMesKwh / meta) * 100;
  let rate: number = ATIVACAO_FAIXAS[0].rate;
  for (const f of ATIVACAO_FAIXAS) {
    if (pct >= f.minPct) rate = f.rate;
  }
  return rate;
}

// ==========================================
// BÔNUS DE 1ª FATURA (safra de clientes fechados)
// ==========================================
// Dos clientes fechados no mês, quando chega o 1º boleto (60–90 dias) o
// vendedor acompanha o pagamento da safra: ≥90% pagos dentro do mês de envio
// → R$ 250; 100% → R$ 500. Pago na competência seguinte à do 1º boleto.
export const SAFRA_BONUS = {
  pct90: 250,
  pct100: 500,
} as const;

// Recorrência: EM ANÁLISE (Bruno 13/07/2026) — condições de recorrência e
// volume p/ fixo serão definidos depois. Enquanto false, o motor não calcula
// e as telas não exibem nada de recorrência/carteira.
export const RECORRENCIA_ATIVA = false;

// ==========================================
// BÔNUS DE THRESHOLD (Filtro 2 — gatilho de volume)
// ==========================================

export const THRESHOLD_FAIXAS = [
  { maxPctMeta: 20, bonus: 0, label: '0 a 5.000 kWh (0–20%)' },
  { maxPctMeta: 50, bonus: 2500, label: '5.001 a 12.500 kWh (21–50%)' },
  { maxPctMeta: Infinity, bonus: 5000, label: 'Acima de 12.500 kWh (>50%)' },
] as const;

export function getThresholdBonus(volumeMesKwh: number): number {
  const pct = (volumeMesKwh / META_MENSAL_KWH) * 100;
  if (pct <= 20) return 0;
  if (pct <= 50) return 2500;
  return 5000;
}

// ==========================================
// ATIVAÇÃO DO MÊS — VENDEDOR EXTERNO (v2), parte pura
// ==========================================
// Mora aqui (e não em comissaoExternaV2.ts) porque aquele arquivo importa o
// cliente supabase e nenhum teste consegue importá-lo. Fonte única da conta de
// ativação + threshold: o fechamento da Folha (calcularFechamentoExternoV2) e a
// barra "Meu Trabalho" do CRM chamam a mesma função.

export interface NegociacaoAprovadaMes {
  id: number;
  responsavel_id: string | null;
  responsavel?: { id: string; role?: string; parceiro_id?: string | null } | null;
  consumo_medio_kwh: number | null;
  fidelidade?: string | null;
  plano_codigo?: string | null;
  publico?: string | null;
  multiplicador_final?: number | null;
  unidades_consumidoras?: Array<{
    numero_uc: string;
    grupo_tarifario?: string | null;
    desconto_tarifa?: number | null;
    desconto_bandeira?: number | null;
  }> | null;
  /** Quem do pós-venda gerou a venda por indicação (#6236): ativação 120%, metade para cada um. */
  pos_venda_id?: string | null;
}

/**
 * Multiplicador da negociação para o comissionamento:
 * 1. snapshot gravado pelo modal Alterar Plano (multiplicador_final);
 * 2. senão, plano inferido dos percentuais da 1ª UC × público (volume) × fidelidade;
 * 3. senão, 1,00x (negociação sem plano — sinalizada na memória de cálculo).
 */
export function resolverMultiplicadorNegociacao(neg: NegociacaoAprovadaMes): { mult: number; origem: 'snapshot' | 'inferido' | 'sem_plano' } {
  if (neg.multiplicador_final != null && Number(neg.multiplicador_final) > 0) {
    return { mult: Number(neg.multiplicador_final), origem: 'snapshot' };
  }
  const kwh = Number(neg.consumo_medio_kwh) || 0;
  if (neg.plano_codigo) {
    const r = calcularMultiplicadorFinal(neg.plano_codigo, kwh, neg.fidelidade);
    return { mult: r.final, origem: 'inferido' };
  }
  const uc = neg.unidades_consumidoras?.[0];
  if (uc && uc.desconto_tarifa != null) {
    const plano = inferirPlanoHistorico((uc.desconto_tarifa || 0) * 100, (uc.desconto_bandeira || 0) * 100);
    if (plano) {
      const publico = classificarPublico(kwh);
      const mult = getMultiplicadorPlano(plano.codigo, publico) * getFatorFidelidade(neg.fidelidade, plano.codigo);
      return { mult: Math.round(mult * 100) / 100, origem: 'inferido' };
    }
  }
  return { mult: 1.0, origem: 'sem_plano' };
}

export interface AtivacaoV2Item {
  negociacao_id: number;
  kwh: number;
  mult: number;
  origem: 'snapshot' | 'inferido' | 'sem_plano';
  /** Parte de quem vendeu (com indicação do pós-venda, 60% da normal). */
  valor: number;
  is_grupo_a: boolean;
  /** Indicação do pós-venda: a ativação normal (100%) e quem fica com a outra metade. */
  valor_normal?: number;
  pos_venda_id?: string | null;
}

/**
 * Ativação (R$/kWh pela faixa de atingimento × multiplicador de cada negociação)
 * e threshold do mês, ANTES do SLA. Grupo A entra na lista com valor 0 e fica
 * fora do volume (aprovação manual do gerente). Quem chama aplica o SLA.
 *
 * Venda indicada pelo pós-venda (#6236): a ativação dela vale 120% e é dividida meio a
 * meio; `valor` fica com a parte do vendedor e a outra vai em `repassesPosVenda`. Os kWh
 * contam inteiros no volume do vendedor. O SLA e o threshold do vendedor não mexem na
 * parte do pós-venda.
 */
export function calcularAtivacaoV2(
  negs: Array<NegociacaoAprovadaMes & { is_grupo_a: boolean }>,
  metaKwh: number
): { metaKwh: number; volumeTotal: number; rate: number; itens: AtivacaoV2Item[]; valorAtivacao: number; valorThreshold: number; repassesPosVenda: RepassePosVenda[] } {
  const meta = metaKwh > 0 ? metaKwh : META_MENSAL_KWH;
  const volumeTotal = negs.filter(n => !n.is_grupo_a).reduce((s, n) => s + (Number(n.consumo_medio_kwh) || 0), 0);
  const rate = getAtivacaoRate(volumeTotal, meta);
  let valorAtivacao = 0;
  const repassesPosVenda: RepassePosVenda[] = [];
  const itens: AtivacaoV2Item[] = negs.map(neg => {
    const kwh = Number(neg.consumo_medio_kwh) || 0;
    const { mult, origem } = resolverMultiplicadorNegociacao(neg);
    const valorNormal = neg.is_grupo_a ? 0 : kwh * rate * mult;
    const repasse = !neg.is_grupo_a && neg.pos_venda_id ? montarRepasse(neg.id, neg.pos_venda_id, kwh, valorNormal) : null;
    if (repasse) repassesPosVenda.push(repasse);
    const valor = repasse ? repasse.parte_vendedor : valorNormal;
    valorAtivacao += valor;
    return {
      negociacao_id: neg.id, kwh, mult, origem, valor, is_grupo_a: neg.is_grupo_a,
      ...(repasse ? { valor_normal: valorNormal, pos_venda_id: repasse.pos_venda_id } : {}),
    };
  });
  return { metaKwh: meta, volumeTotal, rate, itens, valorAtivacao, valorThreshold: getThresholdBonus(volumeTotal), repassesPosVenda };
}

// ==========================================
// BONIFICAÇÃO INDIRETA — REDE DE PARCEIROS (R$/kWh)
// ==========================================

export const REDE_PARCEIRO_FAIXAS = [
  { max: 19999, rate: 0.02, label: 'Até 19.999 kWh' },
  { max: 49999, rate: 0.05, label: '20.000 a 49.999 kWh' },
  { max: 79999, rate: 0.08, label: '50.000 a 79.999 kWh' },
  { max: Infinity, rate: 0.10, label: 'A partir de 80.000 kWh' },
] as const;

export function getRedeParceiroRate(volumeMesParceiroKwh: number): number {
  for (const f of REDE_PARCEIRO_FAIXAS) {
    if (volumeMesParceiroKwh <= f.max) return f.rate;
  }
  return 0.10;
}

// ==========================================
// SLA DE ESFORÇO NO CRM (Filtro 1)
// ==========================================

export const SLA_METAS = {
  visitasMin: 200,
  visitasMax: 240,
  posVendaMin: 5,
  fechamentosMin: 5,
  fechamentosMax: 10,
  parceirosMin: 2,
} as const;

export interface SlaDetalhe {
  visitas: number;
  posVenda: number;
  fechamentos: number;
  parceiros: number;
  ajusteManual?: boolean; // gestor pode marcar "manutenção de parceiros" cumprida
}

export function avaliarSla(d: SlaDetalhe): { cumprido: boolean; itens: Array<{ label: string; realizado: number; meta: number; ok: boolean }> } {
  const itens = [
    { label: 'Visitas no mês', realizado: d.visitas, meta: SLA_METAS.visitasMin, ok: d.visitas >= SLA_METAS.visitasMin },
    { label: 'Visitas de pós-venda', realizado: d.posVenda, meta: SLA_METAS.posVendaMin, ok: d.posVenda >= SLA_METAS.posVendaMin },
    { label: 'Fechamentos', realizado: d.fechamentos, meta: SLA_METAS.fechamentosMin, ok: d.fechamentos >= SLA_METAS.fechamentosMin },
    { label: 'Parceiros captados/mantidos', realizado: d.parceiros, meta: SLA_METAS.parceirosMin, ok: d.parceiros >= SLA_METAS.parceirosMin || !!d.ajusteManual },
  ];
  return { cumprido: itens.every(i => i.ok), itens };
}

// SLA não cumprido → ativação e threshold caem pela metade.
export const SLA_PENALIDADE_FATOR = 0.5;

// ==========================================
// RECORRÊNCIA DA CARTEIRA ATIVA
// ==========================================

export const RECORRENCIA_RS_KWH = 0.01;
export const RECORRENCIA_MESES_CARENCIA = 3; // gera a partir do 3º mês de pagamento do CNPJ

export const CARTEIRA_LIMITES = {
  inadimplenciaMaxPct: 8,     // < 8%
  descontoMedioMaxPct: 24,    // ≤ 24%
  primeiraFaturaPagaMinPct: 90, // ≥ 90% das UCs
  churnMaxPct: 5,             // < 5%
} as const;

export interface CarteiraIndicadores {
  inadimplenciaPct: number;
  descontoMedioPct: number;
  primeiraFaturaPagaPct: number;
  churnPct: number;
  reclamacoesControladas: boolean; // sem fonte no sistema — input manual do gestor
}

export function avaliarCarteira(i: CarteiraIndicadores): { destravada: boolean; itens: Array<{ label: string; ok: boolean; valor: string }> } {
  const itens = [
    { label: `Inadimplência da carteira < ${CARTEIRA_LIMITES.inadimplenciaMaxPct}%`, ok: i.inadimplenciaPct < CARTEIRA_LIMITES.inadimplenciaMaxPct, valor: `${i.inadimplenciaPct.toFixed(1)}%` },
    { label: `Desconto médio até ${CARTEIRA_LIMITES.descontoMedioMaxPct}%`, ok: i.descontoMedioPct <= CARTEIRA_LIMITES.descontoMedioMaxPct, valor: `${i.descontoMedioPct.toFixed(1)}%` },
    { label: `1ª fatura paga em ≥ ${CARTEIRA_LIMITES.primeiraFaturaPagaMinPct}% das UCs`, ok: i.primeiraFaturaPagaPct >= CARTEIRA_LIMITES.primeiraFaturaPagaMinPct, valor: `${i.primeiraFaturaPagaPct.toFixed(0)}%` },
    { label: 'Índice de reclamações controlado', ok: i.reclamacoesControladas, valor: i.reclamacoesControladas ? 'OK' : 'Fora' },
    { label: `Churn inferior a ${CARTEIRA_LIMITES.churnMaxPct}%`, ok: i.churnPct < CARTEIRA_LIMITES.churnMaxPct, valor: `${i.churnPct.toFixed(1)}%` },
  ];
  return { destravada: itens.every(x => x.ok), itens };
}

// ==========================================
// CLAWBACK E PREMIAÇÃO
// ==========================================

export const CLAWBACK_DIAS = 90;

export const PREMIACAO_TRIMESTRAL = {
  primeiroLugar: 8000,
  segundoLugar: 4000,
  elegibilidade: {
    kwhMinTrimestre: 75000,
    inadimplenciaMaxPct: 5,
    primeiraFaturaPagaMinPct: 90,
    slaTodosOsMeses: true,
  },
} as const;

// Reclassificação automática (volume mudou) e o resolvedor async de modalidade
// que depende de negociação por id vivem em politicaComercialDb.ts — as duas
// únicas funções deste subsistema que tocam banco. Ver o comentário lá em cima
// do arquivo pra saber por que a separação existe.
